import format from "date-fns/format";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTable, useExpanded } from "react-table";

import { ApiService, CaseIntegrationMessage } from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";

import DjangoLink from "components/DjangoLink";
import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";

import IntegrationMessageDetail from "./IntegrationMessageDetail";
import IntegrationMessageStatus from "./IntegrationMessageStatus";
import { flattenIntegrationMessages } from "./integrationMessageUtils";
import ReactTableExpandableCell from "../table/ReactTableExpandableCell";
import ReactTableRow from "../table/ReactTableRow";
import SubTable from "../table/SubTable";
import Table from "../table/Table";

import "./IntegrationMessagesTable.css";

const IntegrationMessagesTable = ({
  adminCase,
}: {
  adminCase: CaseAdminDetail;
}) => {
  const [data, setData] = useState<CaseIntegrationMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const isAdmin = useSelector(
    ({ auth }: ApplicationState) => auth.user?.is_superuser
  );

  useEffect(() => {
    if (!data && adminCase.id) {
      setLoading(true);
      ApiService.integrationMessagesAdminCase({ id: adminCase.id })
        .then((integrationMessages) => setData(integrationMessages))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }
  }, [adminCase.id, data]);

  const rows = useMemo(() => flattenIntegrationMessages(data), [data]);

  const columns = useMemo(
    (): any[] => [
      {
        Header: "Type",
        accessor: "label",
        Cell: ({ row }: any) => (
          <ReactTableExpandableCell
            row={row}
            expandedColor="var(--black)"
            canExpand={row.original.detailFields.length > 0}
          >
            <div className="IntegrationMessagesTable__type">
              <span className="IntegrationMessagesTable__typeName">
                {row.original.label}
              </span>
            </div>
          </ReactTableExpandableCell>
        ),
      },
      {
        Header: "Status",
        accessor: "statusText",
        Cell: ({ row }: any) => (
          <IntegrationMessageStatus
            statusText={row.original.statusText}
            statusVariant={row.original.statusVariant}
          />
        ),
      },
      {
        Header: "Sent/Received Date",
        accessor: "created_at",
        Cell: ({ row }: any) => (
          <>
            {row.original.created_at &&
              format(new Date(row.original.created_at), "MM/dd/yyyy, hh:mm a")}
          </>
        ),
      },
      ...(isAdmin
        ? [
            {
              Header: "Django Link",
              accessor: "orderMessageId",
              Cell: ({ row }: any) =>
                row.original.orderMessageId ? (
                  <DjangoLink
                    route={`integration/${row.original.integrationOrderSourceTable}/${row.original.orderMessageId}/change/`}
                    title={`Django Access: ${row.original.label}`}
                  />
                ) : null,
            },
          ]
        : []),
    ],
    []
  );

  const {
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: rows,
      autoResetExpanded: false,
    },
    useExpanded
  );

  if (loading) return <Spinner />;

  if (!rows.length) {
    return <span>No integration messages recorded for this case.</span>;
  }

  return (
    <Table
      className="integrationMessagesTable"
      headerGroups={headerGroups.map((headerGroup, index) => (
        <tr {...headerGroup.getHeaderGroupProps()} key={index}>
          {headerGroup.headers.map((column: any) => (
            <th {...column.getHeaderProps()} key={`column:${column.id}`}>
              {column.render("Header")}
            </th>
          ))}
        </tr>
      ))}
      rows={tableRows.map((row: any) => {
        prepareRow(row);

        return (
          <Fragment key={row.original.id}>
            <ReactTableRow
              row={row}
              canExpand={row.original.detailFields.length > 0}
            />
            {row.isExpanded && (
              <SubTable>
                <IntegrationMessageDetail
                  detailFields={row.original.detailFields}
                />
              </SubTable>
            )}
          </Fragment>
        );
      })}
    />
  );
};

export default IntegrationMessagesTable;
